// components/FirmwareFlasher.tsx
// 펌웨어 한 개를 GitHub 에서 받아 USB(COM 포트)로 보드에 설치하는 패널.
// 설치 방식을 고른다.
//  - 앱만 설치: .merged.bin 에서 app 영역만 골라 쓴다. 보드의 Wi-Fi 설정(NVS), 저장 데이터(SPIFFS)는 유지된다.
//  - 전체 설치 (기본, 공장 초기 모드): .merged.bin 을 0x0 에 쓴다. 부트로더, 파티션표, NVS, 앱, SPIFFS 가 모두 바뀌고 Wi-Fi 설정 등은 지워진다.
// fileUrls 는 앞에서부터 차례로 시도한다 (새 위치 → 옛 위치).
import React, { useEffect, useRef, useState } from 'react';
import { ESPLoader, Transport } from 'esptool-js';

const BAUD = 460800;

// 파티션표는 합쳐진 이미지의 0x8000 에 있다 (항목 32바이트, 최대 0xC00 바이트).
const PARTITION_TABLE_ADDR = 0x8000;
const PARTITION_TABLE_SIZE = 0xc00;
const FLASH_SECTOR = 0x1000;

type InstallMode = 'app' | 'full';

interface Region { offset: number; size: number }

// 합쳐진 이미지의 파티션표에서 app 파티션(factory 또는 ota_0)과 otadata 파티션(있으면)의 위치와 크기를 찾는다.
// OTA 구조(app 2개)인 모델은 otadata 가 어느 app 으로 부팅할지 정하므로, app 을 쓸 때 otadata 도 같이 써서
// 새로 쓴 app(ota_0)으로 부팅하게 해야 한다.
const findPartitions = (img: Uint8Array): { app: Region | null; otadata: Region | null } => {
  const rd32 = (o: number) => ((img[o] | (img[o + 1] << 8) | (img[o + 2] << 16) | (img[o + 3] << 24)) >>> 0);
  let factory: Region | null = null;
  let ota0: Region | null = null;
  let otadata: Region | null = null;
  for (let o = PARTITION_TABLE_ADDR; o < PARTITION_TABLE_ADDR + PARTITION_TABLE_SIZE; o += 32) {
    if (img[o] !== 0xaa || img[o + 1] !== 0x50) break; // 항목이 끝났거나 MD5 구간
    const type = img[o + 2];
    const subtype = img[o + 3];
    const part = { offset: rd32(o + 4), size: rd32(o + 8) };
    if (type === 0x00 && subtype === 0x00 && !factory) factory = part; // app, factory
    else if (type === 0x00 && subtype === 0x10 && !ota0) ota0 = part; // app, ota_0
    else if (type === 0x01 && subtype === 0x00 && !otadata) otadata = part; // data, ota
  }
  return { app: factory || ota0, otadata: factory ? null : otadata };
};

interface Props {
  title: string;
  fileUrls: string[];
  autoStart?: boolean; // true 면 창이 열리자마자 COM 포트 선택 창을 띄운다 (설치 방식을 고를 필요가 없을 때만 쓴다)
}

const MODE_INFO: Record<InstallMode, { label: string; desc: string }> = {
  full: {
    label: '전체 설치 (공장 초기 모드)',
    desc:
      '부트로더, 파티션표, NVS, 앱, SPIFFS 를 모두 새로 씁니다. 처음 쓰는 보드이거나 파티션 구성이 바뀐 펌웨어일 때, ' +
      '또는 설정을 모두 지우고 공장 상태로 되돌리고 싶을 때 선택하세요. Wi-Fi 설정, 인증서, 저장 데이터가 모두 지워지므로 설치 후 Wi-Fi 를 다시 입력해야 합니다.',
  },
  app: {
    label: '앱만 설치',
    desc:
      '프로그램(app)만 새로 씁니다. 보드에 저장된 Wi-Fi 설정, 인증서(NVS), 스케줄 등 저장 데이터(SPIFFS)가 그대로 유지되어 ' +
      '설치 후 Wi-Fi 를 다시 입력할 필요가 없습니다. 보드의 파티션 구성이 새 펌웨어와 같을 때만 가능하며, 다르면 자동으로 알려 드립니다.',
  },
};

const FirmwareFlasher: React.FC<Props> = ({ title, fileUrls, autoStart }) => {
  const [mode, setMode] = useState<InstallMode>('full');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [logs, setLogs] = useState<string[]>([]);
  const logEnd = useRef<HTMLDivElement>(null);

  const supported = typeof navigator !== 'undefined' && !!(navigator as any).serial;

  const addLog = (msg: string) => {
    setLogs((prev) => [...prev.slice(-200), msg]);
    setTimeout(() => logEnd.current?.scrollIntoView({ behavior: 'smooth' }), 0);
  };

  const download = async (): Promise<Uint8Array> => {
    let lastErr = '';
    for (const url of fileUrls) {
      try {
        const res = await fetch(url, { cache: 'no-cache' });
        if (!res.ok) { lastErr = `HTTP ${res.status}`; continue; }
        const data = new Uint8Array(await res.arrayBuffer());
        if (data.length < 100 * 1024) { lastErr = '받은 파일이 너무 작습니다'; continue; }
        return data;
      } catch (e: any) {
        lastErr = e?.message || String(e);
      }
    }
    throw new Error(`펌웨어를 받지 못했습니다 (${lastErr})`);
  };

  const start = async () => {
    const serial = (navigator as any).serial;
    if (!serial || busy) return;

    const installMode = mode;
    setError('');
    setLogs([]);
    setProgress(0);

    let port: any = null;
    let transport: Transport | null = null;
    try {
      // 포트 선택은 버튼을 누른 직후에 해야 브라우저가 허용한다 (다운로드보다 먼저)
      setStatus('COM 포트를 선택하세요');
      port = await serial.requestPort();
      setBusy(true);

      setStatus('GitHub 에서 펌웨어 받는 중...');
      addLog(`GitHub 에서 ${title} 펌웨어를 받습니다. (${MODE_INFO[installMode].label})`);
      const data = await download();
      addLog(`받기 완료 (${(data.length / 1024).toFixed(0)} KB)`);

      setStatus('보드에 연결하는 중... (자동으로 안 되면 BOOT 버튼을 누른 채 시도)');
      const terminal = {
        clean: () => {},
        writeLine: (d: string) => addLog(d),
        write: (d: string) => addLog(d),
      };
      transport = new Transport(port, false);
      const loader = new ESPLoader({ transport, baudrate: BAUD, terminal });
      const chip = await loader.main();
      addLog(`칩: ${chip}`);

      let fileArray: { data: Uint8Array; address: number }[];
      if (installMode === 'full') {
        fileArray = [{ data, address: 0x0 }];
      } else {
        // 앱만 설치: 새 펌웨어의 app 파티션 위치를 찾고, 보드의 파티션표가 같은지 확인한다.
        const { app: part, otadata } = findPartitions(data);
        if (!part || part.offset + part.size > data.length) {
          throw new Error('받은 펌웨어에서 app 파티션을 찾지 못했습니다. "전체 설치"를 선택하세요.');
        }
        setStatus('보드의 파티션 구성을 확인하는 중...');
        const onBoard = await loader.readFlash(PARTITION_TABLE_ADDR, PARTITION_TABLE_SIZE);
        const wanted = data.subarray(PARTITION_TABLE_ADDR, PARTITION_TABLE_ADDR + PARTITION_TABLE_SIZE);
        let same = onBoard.length === wanted.length;
        for (let i = 0; same && i < wanted.length; i++) if (onBoard[i] !== wanted[i]) same = false;
        if (!same) {
          throw new Error('보드의 파티션 구성이 이 펌웨어와 달라 "앱만 설치"를 할 수 없습니다. 설치 방식을 "전체 설치"로 바꿔 다시 시도하세요. (Wi-Fi 설정 등이 지워집니다)');
        }
        // app 영역 끝쪽의 빈 칸(0xFF)은 쓰지 않는다.
        const region = data.subarray(part.offset, part.offset + part.size);
        let last = region.length - 1;
        while (last >= 0 && region[last] === 0xff) last--;
        if (last < 0) throw new Error('app 영역이 비어 있습니다. 펌웨어 파일을 확인하세요.');
        const len = Math.min(region.length, Math.ceil((last + 1) / FLASH_SECTOR) * FLASH_SECTOR);
        addLog(`app 영역 0x${part.offset.toString(16)} 에 ${(len / 1024).toFixed(0)} KB 를 씁니다.`);
        fileArray = [{ data: region.slice(0, len), address: part.offset }];
        // OTA 구조의 모델: otadata 도 새 이미지 것으로 써서 새로 쓴 app(ota_0)으로 부팅하게 한다.
        if (otadata && otadata.offset + otadata.size <= data.length) {
          addLog(`otadata 0x${otadata.offset.toString(16)} (${otadata.size} B)도 함께 씁니다.`);
          fileArray.unshift({ data: data.slice(otadata.offset, otadata.offset + otadata.size), address: otadata.offset });
        }
      }

      setStatus('설치 중... (창을 닫거나 USB 를 뽑지 마세요)');
      await loader.writeFlash({
        fileArray,
        flashMode: 'keep',
        flashFreq: 'keep',
        flashSize: 'keep',
        eraseAll: false,
        compress: true,
        reportProgress: (_i: number, written: number, total: number) => {
          setProgress(Math.round((written / total) * 100));
        },
      });
      await loader.after('hard_reset');
      setProgress(100);
      setStatus(
        installMode === 'full'
          ? '설치 완료! 리셋키를 누르세요. 보드가 재시작합니다. 보드에 빨간불이 켜지면 "연결" 버튼을 누르고 Wi-Fi 설정을 입력하세요.'
          : '설치 완료! 보드가 재시작합니다. 저장된 Wi-Fi 설정은 그대로 유지됩니다.'
      );
      addLog('✅ 설치 완료');
    } catch (e: any) {
      if (e?.name === 'NotFoundError') {
        setStatus(''); // 포트 선택 취소
      } else {
        const raw = e?.message || String(e);
        // 포트를 못 여는 가장 흔한 이유는 다른 프로그램이 같은 포트를 쓰고 있는 것이다
        const msg = /Failed to open serial port/i.test(raw)
          ? 'COM 포트를 열 수 없습니다. 아두이노 시리얼 모니터나 다른 프로그램, 다른 브라우저 탭이 같은 포트를 쓰고 있지 않은지 확인하고 모두 닫은 뒤, USB 를 뽑았다 다시 꽂고 시도하세요.'
          : raw;
        setError(msg);
        setStatus('');
        addLog('❌ ' + raw);
      }
    } finally {
      if (transport) {
        try { await transport.disconnect(); } catch (_) { /* 이미 닫힘 */ }
      }
      setBusy(false);
    }
  };

  // autoStart 가 켜진 경우에만 열리자마자 COM 포트 선택 창을 띄운다 (설치 방식은 기본값인 전체 설치)
  useEffect(() => {
    if (autoStart) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', textAlign: 'left', minWidth: '280px' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>{title} 펌웨어 설치 (USB)</div>
      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '10px' }}>
        PC 의 크롬/엣지에서 보드를 USB 로 연결하세요. 최신 펌웨어를 GitHub 에서 받아 바로 설치합니다.
        시리얼 모니터는 닫아 두세요.
      </div>

      {/* 설치 방식 선택 */}
      <div role="radiogroup" aria-label="설치 방식" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
        {(Object.keys(MODE_INFO) as InstallMode[]).map((m) => {
          const selected = mode === m;
          return (
            <label
              key={m}
              style={{
                display: 'block',
                padding: '10px 12px',
                border: selected ? '2px solid #2563eb' : '1px solid #cbd5e1',
                borderRadius: '8px',
                background: selected ? '#eff6ff' : '#fff',
                cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy && !selected ? 0.6 : 1,
              }}
            >
              <input
                type="radio"
                name={`fw-install-mode-${title}`}
                checked={selected}
                disabled={busy}
                onChange={() => setMode(m)}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontWeight: 600 }}>{MODE_INFO[m].label}</span>
              <div style={{ marginTop: '4px', marginLeft: '24px', fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
                {MODE_INFO[m].desc}
              </div>
            </label>
          );
        })}
      </div>

      {!supported && (
        <div style={{ color: '#b91c1c', marginBottom: '10px' }}>이 브라우저는 Web Serial 을 지원하지 않습니다. (PC 의 Chrome/Edge 필요)</div>
      )}

      <button type="button" className="action-btn btn-primary" onClick={start} disabled={!supported || busy}>
        {busy ? `설치 중... ${progress}%` : 'COM 포트 선택하고 설치 시작'}
      </button>

      {(busy || progress > 0) && (
        <div style={{ marginTop: '10px', height: '10px', background: '#e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: '#3b82f6', transition: 'width 0.2s' }} />
        </div>
      )}
      {status && <div style={{ marginTop: '10px', color: '#2563eb', fontSize: '0.9rem' }}>{status}</div>}
      {error && (
        <div style={{ marginTop: '10px', padding: '10px 12px', background: '#fee2e2', border: '1px solid #ef4444', borderRadius: '8px', color: '#b91c1c', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}
      {logs.length > 0 && (
        <div style={{ marginTop: '10px', maxHeight: '140px', overflowY: 'auto', fontSize: '0.75rem', color: '#475569', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px' }}>
          {logs.map((l, i) => <div key={i}>{l}</div>)}
          <div ref={logEnd} />
        </div>
      )}
    </div>
  );
};

export default FirmwareFlasher;
